import React, { useRef, useState, useMemo, useCallback } from 'react';
import { Tooltip, withStyles } from '@material-ui/core';

// Fallback tooltip width (px) when no valid tooltipMaxWidth is provided.
const DEFAULT_TOOLTIP_MAX_WIDTH = 300;

export default function OverflowTooltipContent({
    text = '',
    toolTipValue = '',
    className = '',
    style = {},
    placement = 'top',
    tooltipMaxWidth = DEFAULT_TOOLTIP_MAX_WIDTH, // default
    lineClamp = 1, // number of lines before text is clipped (1 = single line ellipsis)
    component = 'span', // underlying element: 'span' | 'label' | 'div' | 'p' ...
    isShowTooltip = true, // false: keep overflow/clamp (ellipsis) but never show the tooltip
    onClick,
    children // optional node rendered instead of `text` (tooltip still uses `text`)
}) {
    const textRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [computedMaxWidth, setComputedMaxWidth] = useState(tooltipMaxWidth > 0 ? tooltipMaxWidth : DEFAULT_TOOLTIP_MAX_WIDTH);

    const isMultiLine = lineClamp > 1;

    // Measure lazily on hover (via the tooltip's onOpen) instead of a per-item
    // ResizeObserver. In long lists (hundreds/thousands of rows) observing every element
    // and reading layout up front caused forced synchronous reflow and multi-hundred-ms
    // frames. Reading layout only for the row the user actually points at keeps it cheap,
    // and the element is guaranteed visible at that moment so measurement is reliable.
    //
    // The tooltip is ALWAYS mounted and controlled via `isOpen`: on hover MUI calls
    // onOpen, we measure, and open only when the text really overflows. This keeps the
    // tooltip visible on the FIRST hover (the listener is already attached), unlike a
    // conditional wrapper that mounts only after the first measure.
    const handleOpen = useCallback(() => {
        if (!isShowTooltip) return;

        const el = textRef.current;
        if (!el) return;

        // Single line clips horizontally; multi-line clamp clips vertically.
        const hasOverflow = isMultiLine
            ? el.scrollHeight > el.clientHeight
            : el.scrollWidth > el.clientWidth;

        if (!hasOverflow) return;

        // Guard against null/undefined/non-positive values from call sites that pass
        // tooltipMaxWidth conditionally (e.g. `len > 1000 ? len/3 : null`). Without this,
        // Math.min(null, maxAllowed) collapses to 0, forcing maxWidth:0px so the tooltip
        // wraps one char per line and breaks the layout vertically. Fall back to default.
        const requestedMaxWidth = tooltipMaxWidth > 0 ? tooltipMaxWidth : DEFAULT_TOOLTIP_MAX_WIDTH;
        const maxAllowed = window.innerWidth * 0.9;
        setComputedMaxWidth(Math.min(requestedMaxWidth, maxAllowed));
        setIsOpen(true);
    }, [isShowTooltip, isMultiLine, tooltipMaxWidth]);

    const handleClose = useCallback(() => setIsOpen(false), []);

    const CustomTooltip = useMemo(
        () =>
            withStyles({
                tooltip: {
                    maxWidth: `${computedMaxWidth}px`,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                }
            })(Tooltip),
        [computedMaxWidth]
    );

    const clampStyle = isMultiLine
        ? {
              display: '-webkit-box',
              WebkitLineClamp: lineClamp,
              WebkitBoxOrient: 'vertical',
              whiteSpace: 'normal',
              wordBreak: 'break-all'
          }
        : {
              display: 'inline-block',
              whiteSpace: 'nowrap'
          };

    const Component = component;

    const content = (
        <Component
            ref={textRef}
            className={className}
            onClick={onClick}
            style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                verticalAlign: 'middle',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'left',
                ...clampStyle,
                ...style
            }}
        >
            {children != null ? children : text}
        </Component>
    );

    // Always mount the tooltip and control it with `isOpen`. MUI attaches the hover/focus
    // listeners to `content` on first render, so measuring in onOpen and opening when the
    // text overflows shows the tooltip on the FIRST hover. When isShowTooltip is false,
    // handleOpen returns early so the tooltip never opens.
    return (
        <CustomTooltip
            title={toolTipValue || text}
            placement={placement}
            arrow
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            disableHoverListener={!isShowTooltip}
            disableTouchListener={!isShowTooltip}
            disableFocusListener={!isShowTooltip}
            enterDelay={0}
            enterTouchDelay={0}
        >
            {content}
        </CustomTooltip>
    );
}
